import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import LegalLayout from "../components/legal/LegalLayout";
import { tenantLegalApi } from "../lib/api";

export default function TenantSmsPrivacy() {
  const { slug } = useParams();
  const [info, setInfo] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let active = true;
    tenantLegalApi
      .info(slug)
      .then((d) => active && setInfo(d))
      .catch(() => active && setNotFound(true));
    return () => {
      active = false;
    };
  }, [slug]);

  if (notFound) {
    return (
      <div className="min-h-screen bg-[#0B1220] text-slate-300 flex items-center justify-center" data-testid="tenant-legal-not-found">
        <p>This page could not be found.</p>
      </div>
    );
  }
  if (!info) {
    return (
      <div className="min-h-screen bg-[#0B1220] flex items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
      </div>
    );
  }

  const name = info.businessName;
  return (
    <LegalLayout title={`${name} SMS Privacy Policy`} updated="August 2026">
      <section data-testid="tenant-sms-privacy-content">
        <p>
          This SMS Privacy Policy explains how <strong>{name}</strong> ("the business") collects, uses, and protects your phone number
          and related information when you opt in to receive text messages regarding your appointments and services through our
          service management platform.
        </p>
      </section>
      <section>
        <h2>1. Information We Collect</h2>
        <p>
          When you provide your mobile phone number and agree to receive text messages, we collect your phone number, the timestamp
          and method of your consent (verbal or web form), and the content and delivery status of messages sent to and received from
          that number.
        </p>
      </section>
      <section>
        <h2>2. SMS Program Description</h2>
        <p>
          Our SMS program is used solely for transactional and service-related communications: appointment confirmations, reminders,
          rescheduling notices, and technician arrival/service-status updates. We do not use this program to send marketing or
          promotional messages.
        </p>
      </section>
      <section>
        <h2>3. How We Use Your Mobile Information</h2>
        <p>
          Your phone number and SMS consent status are used only to deliver the service messages described above and to honor
          opt-out (STOP) and opt-in (START) requests.{" "}
          <strong>No mobile information will be shared with third parties or affiliates for marketing or promotional purposes.</strong>{" "}
          Information sharing to subcontractors for support services is permitted in all cases, excluding cases where sharing of
          mobile information is used for marketing purposes.
        </p>
      </section>
      <section>
        <h2>4. Opt-Out and Support</h2>
        <p>
          You can opt out of receiving text messages from {name} at any time by replying <strong>STOP</strong> to any message. Reply{" "}
          <strong>HELP</strong> for assistance.
          {info.supportEmail ? (
            <>
              {" "}
              You may also contact us at <a href={`mailto:${info.supportEmail}`}>{info.supportEmail}</a>.
            </>
          ) : null}
        </p>
      </section>
      <section>
        <h2>5. Data Retention &amp; Security</h2>
        <p>
          We retain SMS consent and message records for as long as necessary to operate the SMS program and meet legal and carrier
          compliance obligations, and protect this information using the same safeguards applied to other customer data on our
          platform.
        </p>
      </section>
    </LegalLayout>
  );
}
